import { useApolloClient } from '@apollo/client';
import { useQuery } from '@tanstack/react-query';

import featureNames from '~/shared/constants/featureFlags';
import { BATCHED_GQL_PRODUCT_PRICING } from '~/shared/constants/queryCacheKeys';
import useHasFeature from '~/shared/utils/useHasFeature';
import { useMembership } from '~/techstyle-shared/react-accounts';

import getGqlPricingDataPricingService from '../api/getGqlPricingDataPricingService';
import getGqlPricingDataPricingServiceV3 from '../api/getGqlPricingDataPricingServiceV3';
import {
  BROWSE_API_PAGE_SIZE,
  FED_GRAPH_PRICING_STALE_TIME,
} from '../constants';

/**
 *
 * @description Hook to fetch product pricing data in batches
 *
 * Leverage the apollo client cache to only query for new batches of masterProductIds.
 * This works well for paginated results where the masterProductIds are expected to be in the same order for the same query params.
 * Apollo client will cache the previous pages (batches) of masterProductIds and only fetch the latest page (batch) of masterProductIds.
 *
 * Example for page size of 5:
 * 1st render: masterProductIds = [1, 2, 3, 4, 5], fetches pricing for [1, 2, 3, 4, 5]
 * 2nd render: masterProductIds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10], fetches pricing for [6, 7, 8, 9, 10]
 * [1, 2, 3, 4, 5] are already cached in the apollo client cache and will not be fetched again unless the apollo cache is invalidated
 * (which currently only happens when the user refreshes the page or starts a new browser session)
 *
 * Assume we SPA navigate to a different page and come back to the original page:
 * 3rd render: masterProductIds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10], fetches pricing for [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
 * Both [1, 2, 3, 4, 5] and [6, 7, 8, 9, 10] batches are already cached in the apollo client cache and will not be fetched again unless the cache is invalidated
 *
 * If we need to bypass the apollo cache and always fetch the latest pricing data, we can use the fetchPolicy: 'network-only' option in the apollo client query
 * and set the staleTime to 0 in the useQuery options.
 *
 * Use the Apollo Client DevTools to inspect the cache and see the cached results for the productPricing query
 *
 * Caveat: If the order of masterProductIds is expected to change, this hook will not work as expected.
 *
 * @param {Array} masterProductIds
 * @returns {Object}
 */
export const useBatchedGqlProductPricing = (masterProductIds = []) => {
  const { data: isPricingServiceV3Enabled } = useHasFeature(
    featureNames.PRICING_SERVICE_V3
  );

  const batches = [];
  for (let i = 0; i < masterProductIds.length; i += BROWSE_API_PAGE_SIZE) {
    batches.push(masterProductIds.slice(i, i + BROWSE_API_PAGE_SIZE));
  }

  const apolloClient = useApolloClient();
  const { isVip } = useMembership();

  return useQuery({
    queryKey: [
      BATCHED_GQL_PRODUCT_PRICING,
      masterProductIds, // Cache based on all requested masterProductIds
      { isVip },
    ],
    queryFn: () =>
      Promise.all(
        batches.map((batchedProductIds) =>
          isPricingServiceV3Enabled
            ? getGqlPricingDataPricingServiceV3({
                apolloClient,
                masterProductIds: batchedProductIds,
                isVip,
              })
            : getGqlPricingDataPricingService({
                apolloClient,
                masterProductIds: batchedProductIds,
                isVip,
              })
        )
      ).then((results) => {
        return results?.flatMap((results) => {
          const productsPricing = results.data?.productPricing || [];
          if (isPricingServiceV3Enabled) {
            const mappedPricing = productsPricing.map((productPricing) => {
              const finalDetails = productPricing.discountDetails.promoPrice
                ? productPricing.discountDetails
                : productPricing.bogoDetails;
              return {
                ...productPricing,
                finalDetails,
              };
            });
            return mappedPricing;
          }
          return productsPricing;
        });
      }),
    enabled: apolloClient && masterProductIds.length > 0, // Only fetch if there are masterProductIds
    staleTime: FED_GRAPH_PRICING_STALE_TIME, // when this stale time is reached, the apollo client cache will be the next layer
    keepPreviousData: true, // Keep previous data while fetching new data to prevent UI flickering from entering loading state while fetching new data
  });
};

export default useBatchedGqlProductPricing;
